import { useState } from 'react';
import {
  useFormContext,
  Controller,
  UseControllerProps,
} from 'react-hook-form';
import { sanitizeInput, TextField, TextFieldProps } from 'material-hu';
import 'src/components/dashboard/form/i18n';
import { useTranslation } from './i18n';

export type FormTextFieldProps = TextFieldProps &
  UseControllerProps & {
    ariaLabel?: string;
    resetOnEmptyBlur?: boolean;
    showErrors?: boolean;
    refValue?: any;
    min?: number;
    max?: number;
    minError?: string;
    maxError?: string;
    showCounter?: boolean;
  };

export const FormTextField = ({
  name,
  refValue,
  rules,
  defaultValue,
  resetOnEmptyBlur,
  onBlur: onBlurProp,
  showErrors = false,
  disabled,
  fullWidth = true,
  maxRows,
  minRows,
  multiline,
  placeholder,
  size,
  label,
  autoFocus,
  type,
  ariaLabel,
  variant,
  onFocus,
  onChange: onChangeProp,
  onKeyDown,
  onPaste,
  InputLabelProps,
  InputProps,
  inputProps,
  margin,
  sx,
  onClick,
  value: valueProp,
  helperText: helperTextProp,
  min,
  max,
  showCounter = false,
  ...others
}: FormTextFieldProps) => {
  const { control, setValue, watch } = useFormContext();
  const [previousState, setPreviousState] = useState('');

  const { t } = useTranslation();

  const val = watch(name);
  const length = val?.length || 0;

  const getHelperText = error => {
    if (helperTextProp) {
      return helperTextProp;
    }
    if (showErrors && error?.message) return error.message;
    if (showCounter) return t('CHARACTERS_COUNTER', { count: length, max });
  };

  return (
    <Controller
      render={({
        field: { ref, onBlur, onChange, value, ...field },
        fieldState: { error },
      }) => (
        <>
          <TextField
            {...field}
            onChange={e => {
              if (onChangeProp) {
                onChangeProp(e);
                return;
              }
              onChange(e);
            }}
            onFocus={e => {
              if (onFocus) {
                onFocus(e);
              }
              if (resetOnEmptyBlur) {
                setPreviousState(value);
              }
            }}
            onBlur={() => {
              onBlur();
              if (onBlurProp) onBlurProp(null);
              if (resetOnEmptyBlur && value === '')
                setValue(field.name, previousState);
            }}
            inputRef={refValue || ref}
            error={!!error}
            size={size}
            disabled={disabled}
            fullWidth={fullWidth}
            maxRows={maxRows}
            minRows={minRows}
            multiline={multiline}
            placeholder={placeholder}
            label={label}
            autoFocus={autoFocus}
            type={type}
            aria-label={ariaLabel}
            variant={variant}
            onKeyDown={onKeyDown}
            onPaste={onPaste}
            InputProps={InputProps}
            InputLabelProps={InputLabelProps}
            inputProps={{
              maxLength: max,
              minLength: min,
              ...inputProps,
            }}
            margin={margin}
            sx={sx}
            onClick={onClick}
            helperText={getHelperText(error)}
            value={valueProp || value}
            {...others}
          />
        </>
      )}
      name={name}
      rules={{
        ...rules,
        validate: {
          sanitize: input => sanitizeInput(input, t('INVALID_CHARACTERS')),
        },
      }}
      control={control}
      defaultValue={defaultValue}
    />
  );
};

export default FormTextField;
